import React, { useMemo, useState } from 'react';
import type { PracticeRulesState } from '../../receptionist-settings/types';
import { RCPT_SETTINGS_ANALYZED_RULES } from '../../receptionist-settings/mockData';
import { Icon } from '../Icon/Icon';
import {
  SettingsDirtyBlock,
  SettingsFieldRow,
  SettingsMiniStat,
  SettingsPageHeader,
  SettingsRuleCard,
  SettingsSectionHeader,
  SettingsSegmented,
  SettingsSelect,
  SettingsSlider,
} from './SettingsShared';

export interface PracticeRulesSettingsPageProps {
  state: PracticeRulesState;
  setState: React.Dispatch<React.SetStateAction<PracticeRulesState>>;
  initial: PracticeRulesState;
}

export function PracticeRulesSettingsPage({ state, setState, initial }: PracticeRulesSettingsPageProps) {
  const upd = (key: keyof PracticeRulesState, value: PracticeRulesState[keyof PracticeRulesState]) =>
    setState((s) => ({ ...s, [key]: value }));
  const dirty = (key: keyof PracticeRulesState) => state[key] !== initial[key];
  const [isAnalyzing, setIsAnalyzing] = useState(false);

  const handleAnalyze = () => {
    setIsAnalyzing(true);
    setTimeout(() => setIsAnalyzing(false), 1400);
  };

  const counts = useMemo(() => {
    const c = { enabled: 0, rejected: 0, needs: 0, conflicts: 1 };
    RCPT_SETTINGS_ANALYZED_RULES.forEach((r) => {
      if (r.status === 'enabled') c.enabled++;
      else if (r.status === 'rejected') c.rejected++;
      else if (r.status === 'needs-config') c.needs++;
    });
    return c;
  }, []);

  return (
    <>
      <SettingsPageHeader
        eyebrow="Behavior · practice rules"
        title="Practice"
        italic="rules"
        description="Write down everything you'd tell a new front-desk hire — in plain English. Elva reads your rules, maps each one to a setting or behavior, and tells you which are applied, which need config, and which fall outside her scope."
      />

      <div className="set-fade-up set-d-1 mb-[26px] grid grid-cols-4 gap-3">
        <SettingsMiniStat label="Enabled" value={counts.enabled} valueColor="var(--set-green-deep)" />
        <SettingsMiniStat label="Needs config" value={counts.needs} valueColor="var(--set-warning)" />
        <SettingsMiniStat label="Rejected" value={counts.rejected} valueColor="var(--set-negative)" />
        <SettingsMiniStat label="Conflicts" value={counts.conflicts} />
      </div>

      <section className="set-fade-up set-d-2 mb-[26px]">
        <div className="set-card">
          <SettingsSectionHeader
            num="01"
            title="How should Elva behave at this practice?"
            sub="One rule per paragraph works best. No special syntax needed — write in plain English."
            action={{ label: isAnalyzing ? 'Analyzing…' : 'Re-analyze rules', onClick: handleAnalyze }}
            actionType="pill"
          />
          <SettingsDirtyBlock isDirty={dirty('rules')} className="px-6 py-5">
            <textarea
              className="set-textarea min-h-[260px] text-[14px] leading-[1.65]"
              value={state.rules}
              onChange={(e) => upd('rules', e.target.value)}
              rows={11}
            />
            <div className="mt-2.5 flex items-center gap-2 text-[12px] text-[var(--ink-2)]">
              <Icon kind="clock" size={11} stroke={2} />
              Last analyzed {state.lastAnalyzed}
            </div>
          </SettingsDirtyBlock>
        </div>
      </section>

      <section className="set-fade-up set-d-3 mb-[26px]">
        <div className="set-card">
          <SettingsSectionHeader
            num="02"
            title="What Elva found in your rules"
            sub="Each rule below was extracted from your text and mapped to where it lives in Elva's settings. Click through to view or apply."
          />
          <div className="flex flex-col gap-3 px-6 py-[18px]">
            {RCPT_SETTINGS_ANALYZED_RULES.map((r, i) => (
              <SettingsRuleCard key={i} {...r} />
            ))}
          </div>
        </div>
      </section>

      <section className="set-fade-up set-d-4 mb-[26px]">
        <div className="set-card">
          <SettingsSectionHeader
            num="03"
            title="How should Elva respond when a patient tries to"
            italic="cancel?"
            sub="These are direct policy controls — Elva applies them on every cancellation call, regardless of what's in your written rules above."
          />
          <SettingsFieldRow
            title="Penalty window"
            description="How far in advance must patients cancel before a late-cancellation fee applies?"
            isDirty={dirty('penaltyWindow')}
          >
            <SettingsSelect
              value={state.penaltyWindow}
              onChange={(v) => upd('penaltyWindow', v)}
              options={[
                { value: '12', label: '12 hours' },
                { value: '24', label: '24 hours' },
                { value: '48', label: '48 hours' },
                { value: '72', label: '72 hours' },
              ]}
            />
          </SettingsFieldRow>
          <SettingsFieldRow
            title="Late enforcement tone"
            description="The phrasing Elva uses when a patient cancels inside the penalty window. Soft guilt reminds patients of the policy; strict policy quotes the fee directly."
            isDirty={dirty('enforcementTone')}
          >
            <SettingsSegmented
              options={[
                { id: 'soft', label: 'Soft guilt', subtitle: "remind, don't fine" },
                { id: 'strict', label: 'Strict policy', subtitle: 'quote the fee' },
              ]}
              value={state.enforcementTone}
              onChange={(v) => upd('enforcementTone', v as 'soft' | 'strict')}
            />
          </SettingsFieldRow>
          <SettingsFieldRow
            title="Save aggressiveness"
            description="How many times Elva tries to reschedule the patient before letting them cancel outright."
            isDirty={dirty('saveAggressiveness')}
            last
          >
            <div className="flex items-center gap-3.5">
              <SettingsSlider
                value={state.saveAggressiveness}
                onChange={(v) => upd('saveAggressiveness', v)}
                min={0}
                max={5}
                leftLabel="Don't try"
                rightLabel="Try 5×"
              />
              <span className="shrink-0 text-[12px] font-semibold text-[var(--ink-1)]">
                {state.saveAggressiveness} attempts to reschedule
              </span>
            </div>
          </SettingsFieldRow>
        </div>
      </section>
    </>
  );
}
